import BookOpen from "@/assets/icons/book-open.svg";
import ChevronDown from "@/assets/icons/chevron-down.svg";
import ChevronRight from "@/assets/icons/chevron-right.svg";
import ChevronUp from "@/assets/icons/chevron-up.svg";
import { useI18n } from "@/contexts/i18nContext";
import { useToggle } from "@/hooks/useToggle";
import { Mention, SimilarMention } from "@/types/Mention";
import { pluralize } from "@/utils";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import TextButton from "@components/TextButton";

import { useEpisode } from "../../../context";
import Favorite from "../../Favorite";
import Footer from "../../Footer";
import Header from "../../Header";
import Actions from "../../Mention/Actions";
import RemoveMention from "../../Mention/RemoveMention";
import Topic from "../../Mention/Topic";
import PaywallNotice from "../../PaywallNotice";
import ShareEpisode from "../../ShareEpisode";
import Translate from "../../Translate";
import ExternalLink from "../partials/ExternalLink";
import MentionComponent from "./Mention";
import SimilarNewsArticle from "./SimilarNewsArticle";

import styles from "./News.module.css";

// Flatten the arrays of similar mentions
const flattenSimilar = (mentions: Mention[]): SimilarMention[] => {
  return mentions.map((m) => m.similar || []).flat(2);
};

const NewsEpisodeBody = () => {
  const episode = useEpisode();
  const { language } = useI18n();
  const {
    hideActions,
    can_mark_favorite,
    textMentions,
    channel_language,
    setReaderModalOpen,
  } = episode;
  const [collapsed, toggleCollapsed] = useToggle(true);
  const [similarOpen, toggleSimilarOpen] = useToggle(false);

  const similar: SimilarMention[] = flattenSimilar(textMentions);

  const renderMentions = collapsed ? textMentions.slice(0, 1) : textMentions;

  const channelNotInUserLanguage = channel_language !== language;

  return (
    <div className={styles.root} data-testid="audio-episode">
      <div className={styles.inner}>
        <Header />
        <PaywallNotice />
        {renderMentions.map((m, i) => (
          <MentionComponent mention={m} key={i} />
        ))}
        <div className={styles.lower}>
          <div>
            <div className={styles.actions}>
              {textMentions.length > 1 && (
                <TextButton
                  onClick={toggleCollapsed}
                  icon={collapsed ? ChevronRight : ChevronUp}
                >
                  {L.MENTIONS_NEWS_VIEW_ALL_MENTIONS.replace(
                    "{}",
                    textMentions.length
                  )}
                </TextButton>
              )}
              <Topic mention={{ ...textMentions[0] }} />
              {episode.article_text && (
                <TextButton
                  icon={BookOpen}
                  data-testid="full-article-button"
                  onClick={() => setReaderModalOpen(true)}
                >
                  {L.MENTIONS_EXTERNAL_LINK_READ_LOCALLY}
                </TextButton>
              )}
              {/** External Link should receive some copy change when the full article is available: */}
              {channelNotInUserLanguage &&
                textMentions.length > 0 &&
                !hideActions && (
                  <Translate mentions={textMentions} toKey="trimmed_content" />
                )}
              <ExternalLink />

              {!hideActions && (
                <>
                  <AccessControl level={ACCESS_LEVELS.USER}>
                    <RemoveMention />
                  </AccessControl>
                  <Actions />
                </>
              )}
            </div>

            <Footer />
            {similar.length > 0 && (
              <TextButton
                data-testid="test-similar-mentions"
                onClick={toggleSimilarOpen}
                className={styles.toggleSimilar}
                color="inherit"
                icon={similarOpen ? ChevronUp : ChevronDown}
              >
                {pluralize(
                  similar.length,
                  L.MENTIONS_SIMILAR_NEWS_LINK_SINGULAR,
                  L.MENTIONS_SIMILAR_NEWS_LINK_PLURAL.replace(
                    "{}",
                    similar.length
                  )
                )}
              </TextButton>
            )}
          </div>
          <ShareEpisode className={styles.share} />
        </div>
        {similarOpen && (
          <div className={styles.similarGrid}>
            {similar.map((s) => (
              <SimilarNewsArticle {...s} key={s.uuid} />
            ))}
          </div>
        )}
      </div>

      {!hideActions && can_mark_favorite && (
        <AccessControl>
          <Favorite className={styles.favorite} />
        </AccessControl>
      )}
    </div>
  );
};

export default NewsEpisodeBody;
